import { DAY_MS, isoWeek, weekStartMs } from './boards';

/**
 * Holding bonuses (docs/ONLINE.md, "Boards"; owner, 2026-10-25): coins for ending a day or a week
 * in a board's top 10, and a crown for ending the featured week first. The server works them out
 * from the boards once a period is over (GET /me/awards); the game pays each one once.
 */

/** Places that earn a bonus. */
export const AWARD_TOP = 10;

/** Coins for each kind of bonus. The crown pays its paint instead (once). */
export const AWARD_COINS = { daily: 10, endless: 20, featured: 30, crown: 0 } as const;

export type AwardKind = keyof typeof AWARD_COINS;

/** Days of Dailies and weeks the server looks back over (a phone away longer misses the rest). */
export const AWARD_DAYS = 7;
export const AWARD_WEEKS = 2;

/** Award ids the save keeps as paid (more than the server ever answers with at once). */
const PAID_KEPT = 100;

/**
 * One bonus: `daily:2026-10-24`, `endless:rain:2026-W43`, `featured:2026-W43`, `crown:2026-W43`.
 * The id is the same every time the server answers, so it pays once.
 */
export interface Award {
  id: string;
  kind: AwardKind;
}

/**
 * The Dailies that are over everywhere (their day has ended in UTC-12, at 12:00 UTC the day after),
 * newest first, at most `days` of them.
 */
export function finishedDailies(nowMs: number, days = AWARD_DAYS): string[] {
  // The newest date whose start is at least a day and 12 hours ago.
  const newest = Math.floor((nowMs - DAY_MS - 12 * 60 * 60 * 1000) / DAY_MS);
  return Array.from({ length: days }, (_, i) =>
    new Date((newest - i) * DAY_MS).toISOString().slice(0, 10),
  );
}

/** The ISO weeks that are over, newest first, at most `weeks` of them. */
export function finishedWeeks(nowMs: number, weeks = AWARD_WEEKS): string[] {
  const current = weekStartMs(isoWeek(nowMs)) ?? nowMs;
  return Array.from({ length: weeks }, (_, i) => isoWeek(current - (i * 7 + 1) * DAY_MS));
}

export function dailyAward(date: string): Award {
  return { id: `daily:${date}`, kind: 'daily' };
}
export function endlessAward(mode: string, week: string): Award {
  return { id: `endless:${mode}:${week}`, kind: 'endless' };
}
export function featuredAward(week: string): Award {
  return { id: `featured:${week}`, kind: 'featured' };
}
export function crownAward(week: string): Award {
  return { id: `crown:${week}`, kind: 'crown' };
}

/** What the server's awards add up to for this player, and the paid list to keep after. */
export interface AwardPayout {
  coins: number;
  /** New bonuses, by kind (for the message). */
  counts: Partial<Record<AwardKind, number>>;
  /** A featured week won (the crown's paint, if the player does not have it yet). */
  crown: boolean;
  paid: string[];
}

/** Pays the awards not paid before. */
export function settleAwards(paid: readonly string[], awards: readonly Award[]): AwardPayout {
  const done = new Set(paid);
  const fresh = awards.filter((a) => !done.has(a.id));
  const counts: AwardPayout['counts'] = {};
  for (const a of fresh) counts[a.kind] = (counts[a.kind] ?? 0) + 1;
  const next = [...paid, ...new Set(fresh.map((a) => a.id))];
  return {
    coins: fresh.reduce((sum, a) => sum + AWARD_COINS[a.kind], 0),
    counts,
    crown: fresh.some((a) => a.kind === 'crown'),
    paid: next.slice(-PAID_KEPT),
  };
}
